import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@navix/auth-ui'
import { useAuthStore } from './stores/authStore'
import { usePermissionStore } from './stores/permissionStore'
import { resetAllStores } from './stores/resetStores'
import { useCanAccess } from './hooks/useCanAccess'
import LoginPage from './pages/LoginPage'
import ViewerPage from './pages/ViewerPage'
import AdminPage from './pages/AdminPage'
import MainLayout from './components/layout/MainLayout'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'

function AdminGuard({ children }: { children: React.ReactNode }) {
  const canAccess = useCanAccess('clients', 'read')
  if (!canAccess) return <Navigate to="/" replace />
  return <>{children}</>
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
})

function SessionManager({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token)
  const loaded = usePermissionStore((s) => s.loaded)
  const fetchPermissions = usePermissionStore((s) => s.fetchPermissions)

  useEffect(() => {
    if (token && !loaded) {
      fetchPermissions()
    }
    if (!token) {
      resetAllStores()
    }
  }, [token, loaded, fetchPermissions])

  return <>{children}</>
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <SessionManager>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route element={<ProtectedRoute />}>
                  <Route element={<MainLayout />}>
                    <Route path="/" element={<ViewerPage />} />
                    <Route path="/admin" element={<AdminGuard><AdminPage /></AdminGuard>} />
                  </Route>
                </Route>
              </Routes>
            </BrowserRouter>
          </SessionManager>
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}
